import { FC, useEffect, useMemo, useState } from 'react'

import { Box, BoxProps, CSSProperties, Stack } from '@mui/material'
import {
  ChartDataProvider,
  ChartDataProviderProps,
  ChartsSurface,
  ChartsSurfaceProps,
  ChartsTooltip,
  ChartsTooltipProps,
  HighlightItemData,
  PiePlot,
  PiePlotProps,
  PieSeriesType
} from '@mui/x-charts'

import { InteractiveLegend } from './InteractiveLegend'
import { ChartDataItem } from './types'

export const PIE_CHART_TEST_ID = 'pie-chart' as const

type PieChartProps = {
  chartData: ChartDataItem[]
  /** Props for the Pie Series, `data` and `type` properties are unavailable. */
  pieSeriesProps?: Omit<PieSeriesType, 'data' | 'type'>
  /** Callback which is fired on PieChart item hover. */
  onHighlightChange?: (item: ChartDataItem | null) => void
  propsOverride?: {
    chartDataProvider?: Omit<ChartDataProviderProps, 'onHighlightChange'> & {
      /** DO NOT USE. Use the one provided in props. */
      onHighlightChange: never
    }
    piePlot?: PiePlotProps
    chartsTooltip?: ChartsTooltipProps
    chartsSurface?: ChartsSurfaceProps
  }
} & BoxProps

export const PieChart: FC<PieChartProps> = ({
  chartData: data,
  propsOverride,
  pieSeriesProps,
  onHighlightChange: propsOnHighlightChange,
  ...props
}) => {
  const [chartData, setChartData] = useState(data)

  const series = propsOverride?.chartDataProvider?.series
    ? propsOverride.chartDataProvider.series
    : []

  const handleOnHighlightChange = (item: HighlightItemData | null) => {
    if (!propsOnHighlightChange) {
      return
    }

    if (item === null) {
      propsOnHighlightChange(null)
      return
    }

    if (item.dataIndex === undefined) {
      return
    }

    const filteredChartData = chartData.filter((value) => !!value.visible)
    propsOnHighlightChange(filteredChartData[item.dataIndex])
  }

  useEffect(() => {
    setChartData(data)
  }, [data])

  const chartLabelsStyles = useMemo(() => {
    return chartData
      .filter((val) => !!val.visible)
      .reduce(
        (acc, value, index) => {
          const key = `.MuiPieArcLabel-root:nth-child(${index + 1})`

          acc[key] = {
            fill: value.textColor,
            fontSize: 14,
            paintOrder: 'stroke',
            letterSpacing: 1
          }

          return acc
        },

        {} as Record<string, CSSProperties>
      )
  }, [chartData])

  return (
    <Box data-testid={PIE_CHART_TEST_ID} width="100%" mx="auto" {...props}>
      <ChartDataProvider
        height={500}
        onHighlightChange={handleOnHighlightChange}
        series={[
          {
            data: chartData.filter((item) => item.visible === true),
            highlightScope: { fade: 'global', highlight: 'item' },
            faded: { additionalRadius: -10 },
            arcLabel: (params) => params.label ?? 'Unknown Sport',
            arcLabelMinAngle: 20,
            type: 'pie',
            ...pieSeriesProps
          },
          ...series
        ]}
        {...propsOverride?.chartDataProvider}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <ChartsSurface
            sx={{
              flex: 1,
              p: 5,
              ...chartLabelsStyles
            }}
            {...propsOverride?.chartsSurface}
          >
            <PiePlot {...propsOverride?.piePlot} />
          </ChartsSurface>
          <ChartsTooltip trigger="item" {...propsOverride?.chartsTooltip} />
          <Stack flex={1} alignItems="center" justifyContent="center">
            <InteractiveLegend
              onItemClick={(chartDataItem) =>
                setChartData((prev) =>
                  prev.map((item) =>
                    item.id === chartDataItem.id
                      ? {
                          ...item,
                          visible: !item.visible
                        }
                      : item
                  )
                )
              }
              data={chartData}
            />
          </Stack>
        </Stack>
      </ChartDataProvider>
    </Box>
  )
}
